import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiPrivate } from "@/lib/api";
import ChatbotWidget from "@/components/chatbot/ChatbotWidget";
import { LayoutDashboard, User, FileText, HelpCircle, Bell, LogOut, ShieldCheck, Search, Users as UsersIcon, MessageCircle, Briefcase, Send, Activity, ClipboardCheck, TrendingUp, ArrowUpRight, Menu, X, Calendar, Shield } from "lucide-react";
import NotificationBell from "@/components/layout/NotificationBell";

export default function AppLayout({ children, admin = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!admin) {
            apiPrivate.get("/notifications").then(r => {
                setUnread(r.data.filter(n => !n.read).length);
            }).catch(() => {});
        }
    }, [admin]);

    const handleLogout = async () => { await logout(); navigate("/"); };

    const socioLinks = [
        { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
        { to: "/app/profilo", label: "Il mio profilo", icon: User, testid: "nav-profilo" },
        { to: "/app/investimento", label: "Il mio investimento", icon: TrendingUp, testid: "nav-investimento" },
        { to: "/app/documenti", label: "Documenti", icon: FileText, testid: "nav-documenti" },
        { to: "/app/mercato", label: "Bacheca annunci soci", icon: Briefcase, testid: "nav-mercato" },
        { to: "/app/assemblee", label: "Assemblee", icon: Calendar, testid: "nav-assemblee" },
        { to: "/app/faq", label: "FAQ", icon: HelpCircle, testid: "nav-faq" },
        { to: "/app/notifiche", label: "Notifiche", icon: Bell, testid: "nav-notifiche", badge: unread },
    ];
    const adminLinks = [
        { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "anav-overview" },
        { to: "/admin/soci", label: "Soci", icon: User, testid: "anav-soci" },
        { to: "/admin/documenti", label: "Documenti", icon: FileText, testid: "anav-docs" },
        { to: "/admin/communications", label: "Comunicazioni", icon: Send, testid: "anav-communications" },
        { to: "/admin/reminders", label: "Promemoria", icon: Bell, testid: "anav-reminders" },
        { to: "/admin/surveys", label: "Sondaggi", icon: ClipboardCheck, testid: "anav-surveys" },
        { to: "/admin/faq", label: "FAQ", icon: HelpCircle, testid: "anav-faq" },
        { to: "/admin/richieste", label: "Richieste accesso", icon: ShieldCheck, testid: "anav-requests" },
        { to: "/admin/chatbot", label: "Chatbot", icon: MessageCircle, testid: "anav-chatbot" },
        { to: "/admin/mercato", label: "Bacheca annunci soci", icon: Briefcase, testid: "anav-mercato" },
        { to: "/admin/assemblies", label: "Assemblee", icon: Calendar, testid: "anav-assemblies" },
        { to: "/admin/governance", label: "Governance", icon: UsersIcon, testid: "anav-governance" },
        { to: "/admin/seo", label: "SEO & Social", icon: Search, testid: "anav-seo" },
        { to: "/admin/audit", label: "Audit trail", icon: Activity, testid: "anav-audit" },
        { to: "/admin/admins", label: "Amministratori", icon: Shield, testid: "anav-admins" },
    ];
    const links = admin ? adminLinks : socioLinks;

    return (
        <div className="min-h-screen flex bg-[#050505] text-white" data-testid={admin ? "admin-layout" : "socio-layout"}>
            {/* Sidebar */}
            <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0a0a0a] border-r border-zinc-900 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
                <div className="p-6 border-b border-zinc-900 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="sidebar-logo">
                        <div className={`h-6 w-6 ${admin ? "bg-magenta" : "bg-acid"}`} />
                        <span className="font-display text-lg leading-none">
                            StartupItalia
                            <div className="text-xs font-grotesk uppercase tracking-widest text-zinc-500 mt-1">
                                {admin ? "Admin Console" : "Investor Area"}
                            </div>
                        </span>
                    </Link>
                    <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-500" data-testid="sidebar-close">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.testid}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `group flex items-center justify-between px-3 py-3 font-grotesk text-sm tracking-wide transition border-l-2 ${
                                    isActive ? "bg-[#121212] border-acid text-white" : "border-transparent text-zinc-400 hover:text-white hover:border-zinc-700"
                                }`
                            }
                        >
                            <span className="flex items-center gap-3">
                                <l.icon className="h-4 w-4" />
                                {l.label}
                            </span>
                            {l.badge > 0 && (
                                <span className="bg-magenta text-black text-[10px] font-bold px-2 py-0.5" data-testid="notif-badge">{l.badge}</span>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-zinc-900">
                    <div className="text-xs text-zinc-500 mb-3 font-grotesk uppercase tracking-widest">Account</div>
                    <div className="text-sm font-grotesk truncate" data-testid="sidebar-user-email">{user?.email}</div>
                    {!admin && user?.quota_categories?.length > 0 && (
                        <div className="mt-2 flex gap-1">
                            {user.quota_categories.map(q => (
                                <span key={q} className="text-[10px] font-bold bg-acid text-black px-2 py-0.5 font-grotesk" data-testid={`quota-badge-${q}`}>Quota {q}</span>
                            ))}
                        </div>
                    )}
                    <button onClick={handleLogout} className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-magenta transition" data-testid="logout-btn">
                        <LogOut className="h-3.5 w-3.5" /> Esci
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0">
                <header className="hidden lg:flex items-center justify-end p-4 border-b border-zinc-900/50 gap-2">
                    <NotificationBell />
                </header>
                <header className="lg:hidden flex items-center justify-between p-4 border-b border-zinc-900">
                    <button onClick={() => setOpen(true)} className="text-white" data-testid="sidebar-open"><Menu className="h-6 w-6" /></button>
                    <div className="flex items-center gap-2">
                        <NotificationBell />
                        <div className={`h-6 w-6 ${admin ? "bg-magenta" : "bg-acid"}`} />
                    </div>
                </header>
                <main className="p-6 lg:p-10">
                    {children}
                </main>
            </div>
            {!admin && <ChatbotWidget />}
        </div>
    );
}
